import { useCallback, useRef } from "react";

const sayLocal = (text) => new Promise((resolve) => {
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.onend = resolve; u.onerror = resolve;
    window.speechSynthesis.speak(u);
  } catch { resolve(); }
});
const stopLocal = () => { try { window.speechSynthesis?.cancel(); } catch { /* none */ } };

// The ONE speech channel for a kiosk session. It decides WHEN a line plays, never WHAT it says:
// every line comes from the server (owner's saved wording). One line at a time; a new line or cancel()
// supersedes everything older (queued or playing); `once` keys never repeat within a session.
export function useSpeech({ engine, mutedRef, volume }) {
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const token = useRef(0);
  const chain = useRef(Promise.resolve());
  const spoken = useRef(new Set());

  const cancel = useCallback(() => { token.current += 1; engineRef.current.interrupt(); stopLocal(); }, []);

  // steps: [{ load: () => Promise<{clips?, audio_b64?, text?}>, once?: key, relevant?: () => bool, local?: bool, report?: (key, outcome) => void }]
  // A step may return several clips (one per saved message): they play one after another, never merged.
  const run = useCallback((steps, { queue = false } = {}) => {
    const prev = queue ? chain.current : null;
    if (!queue) cancel();
    const t = token.current;
    const todo = steps.filter((s) => !s.once || !spoken.current.has(s.once));
    todo.forEach((s) => s.once && spoken.current.add(s.once));
    const loads = todo.map((s) => Promise.resolve().then(s.load).catch(() => null)); // fetch all now, play in order
    const live = () => t === token.current && !mutedRef.current;
    const ended = () => (mutedRef.current ? "muted" : "cancelled");
    const p = (async () => {
      if (prev) await prev;
      for (let i = 0; i < todo.length; i += 1) {
        const st = todo[i];
        const r = await loads[i];
        const clips = r?.clips?.length ? r.clips : r?.audio_b64 ? [{ key: st.key, audio_b64: r.audio_b64 }] : [];
        const stale = st.relevant && !st.relevant();
        for (const c of clips) {
          if (!live() || stale) { st.report?.(c.key, stale ? "cancelled" : ended()); continue; }
          engineRef.current.interrupt(); stopLocal();
          const res = await engineRef.current.play(c.audio_b64, { listenAfter: false, volume });
          st.report?.(c.key, mutedRef.current ? "muted" : res === "ended" ? "completed" : res === "blocked" || res === "error" ? "skipped" : "interrupted");
        }
        if (!clips.length && r?.text && st.local && live() && !stale) { engineRef.current.interrupt(); stopLocal(); await sayLocal(r.text); }
        if (!live()) { todo.slice(i + 1).forEach((x, j) => loads[i + 1 + j].then((rr) => (rr?.clips?.length ? rr.clips.map((c) => c.key) : [x.key]).forEach((kk) => x.report?.(kk, ended())))); return; }
      }
    })();
    chain.current = p.catch(() => {});
    return p;
  }, [cancel, mutedRef, volume]);

  const forget = useCallback((key) => spoken.current.delete(key), []);
  const newSession = useCallback(() => { cancel(); spoken.current = new Set(); }, [cancel]);
  return { run, cancel, forget, newSession };
}
